/**
 * useResolveConflict — the WRITE half of the §3.6 scalar-conflict deconfliction
 * (issue #4007). The read-only indicator (ContactConflictBadges) surfaces a field
 * whose value diverges across provider accounts; this hook lets the user pick the
 * canonical value with one click ("Use this value").
 *
 * On resolve it does an ETag read-modify-write PATCH /contacts/{id} (via the
 * existing `useUpdateObject` → `updateObject`) that writes BOTH:
 *   1. the chosen scalar value at its canonical key (e.g. jobTitle: 'CEO'), and
 *   2. a CLEARED `fieldProvenance` map — the resolved field collapsed to
 *      `{ conflicted: false, candidates: [chosen] }`, every other field's
 *      provenance preserved.
 *
 * Both are sent as TOP-LEVEL body keys (NOT nested under `attributes`): the
 * contacts PATCH handler (apps/api objects.router) strips only a few top-level
 * fields (title/name/status/links) and spreads EVERYTHING else into the attributes
 * bag — so a nested `attributes: {...}` would land at `overflow.attributes`, while a
 * top-level `fieldProvenance` lands correctly at `attributes.fieldProvenance` (it is
 * a canonical key, so it survives the canonicalize chokepoint untouched). This
 * mirrors how the existing contact save sends `organization`/`name` top-level.
 *
 * `objectService.update` stamps `localDirtyAt` on every contact edit — exactly the
 * user-pinned signal the backend writer (`@xavior/persistence` contact-sync
 * `mergeScalar`) honors so a later provider sync never re-flags the field (AC#3). On
 * success `useUpdateObject` already invalidates `queryKeys.objects.byType('contact')`,
 * so the contact refetches and the conflict chip disappears (AC#2). No new endpoint.
 */
import { useCallback } from 'react';
import { useUpdateObject } from '@/features/objects/hooks/index';
import type { ElementSource } from '../contactProvenance';

/** A single conflicted scalar field's stored provenance (the lifted top-level shape). */
interface StoredFieldProvenance {
  conflicted?: boolean;
  candidates?: Array<{ value?: unknown; sources?: ElementSource[] }>;
}

/** The minimal contact shape the resolver needs: an id, an ETag for the optimistic
 *  read-modify-write, and the lifted top-level fieldProvenance map. */
export interface ResolvableContact {
  id: string;
  _etag?: string;
  version?: number | string;
  fieldProvenance?: Record<string, StoredFieldProvenance>;
}

export interface UseResolveConflict {
  resolveConflict: (contact: ResolvableContact, field: string, value: string) => void;
  isResolving: boolean;
}

export function useResolveConflict(): UseResolveConflict {
  const updateContact = useUpdateObject();

  const resolveConflict = useCallback(
    (contact: ResolvableContact, field: string, value: string) => {
      if (!contact?.id) return;

      // Collapse the resolved field to the single chosen value; preserve every other
      // field's provenance verbatim. The candidate keeps its source attribution if the
      // chosen value matched a recorded candidate (so the picker still shows provenance);
      // otherwise it stands alone (sourceless), which is fine — the field is resolved.
      const existing = contact.fieldProvenance ?? {};
      const chosenCandidate =
        existing[field]?.candidates?.find((c) => String(c?.value ?? '').trim() === value) ?? { value };
      const nextFieldProvenance = {
        ...existing,
        [field]: {
          conflicted: false,
          candidates: [{ value, sources: chosenCandidate.sources ?? [] }],
        },
      };

      updateContact.mutate({
        objectType: 'contact',
        objectId: contact.id,
        // Both keys TOP-LEVEL: the contacts PATCH handler spreads non-stripped body
        // fields into the attributes bag, and objectService.update shallow-merges
        // them — so `fieldProvenance` (a canonical key) is rewritten WHOLE (the full
        // map, resolved field cleared) and the chosen scalar lands at its canonical
        // key. A nested `attributes:{}` would mis-route to overflow.
        data: {
          [field]: value,
          fieldProvenance: nextFieldProvenance,
        } as Record<string, unknown>,
        etag: contact._etag || String(contact.version ?? ''),
      });
    },
    [updateContact],
  );

  return { resolveConflict, isResolving: updateContact.isPending };
}
